import {AsideForm} from '@/components/AsideForm';
import {ExpiryDateField} from '@/components/ExpiryDateField';
import {LabeledGridItem} from '@/components/LabeledGridItem';
import {UpdateWithoutConfirmButton} from '@/components/UpdateWithoutConfirmButton';
import {formatUserDisplayName} from '@/utils/formatUserDisplayName';
import {LicenseStatus} from '@/types';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import {
    licenseStatusChipProps,
    StatusChip,
} from '@/components/StatusChip';
import {alpha, Box, Divider, Grid, Typography} from '@mui/material';
import type {Theme} from '@mui/material/styles';
import dayjs from 'dayjs';
import {
    Edit,
    FunctionField,
    ReferenceField,
    SimpleForm,
    TextField,
    Toolbar,
    useEditContext,
    useNotify,
    useRecordContext,
    useRedirect,
} from 'react-admin';
import {useWatch} from 'react-hook-form';

const shellSx = (theme: Theme) => ({
    borderRadius: 0,
    border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
    boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.05)}`,
    overflow: 'hidden',
});

const Title = () => {
    const {record} = useEditContext();
    if (record && 'organisation' in record) {
        return `Licenses / ${record.organisation}`;
    }
    return 'Licenses';
};

const LicenseToolbar = () => {
    const redirect = useRedirect();
    const notify = useNotify();
    const status = useWatch({name: 'status'});
    const record = useRecordContext();

    const expiry = record?.expiry;

    const isExpired = !expiry || dayjs(expiry).isBefore(dayjs(), 'day');
    const showApprove =
        (status === LicenseStatus.PENDING ||
            status === LicenseStatus.REJECTED) &&
        !isExpired;
    const showReject =
        (status === LicenseStatus.PENDING ||
            status === LicenseStatus.APPROVED) &&
        !isExpired;

    const mutationOptions = {
        onSuccess: () => {
            notify('ra.notification.updated', {
                type: 'info',
                messageArgs: {smart_count: 1},
                undoable: false,
            });
            redirect('/licenses');
        },
    };

    return (
        <Toolbar
            disableGutters
            sx={(theme) => ({
                display: 'block',
                width: '100%',
                px: 2.5,
                py: 2.5,
                mt: 2,
                borderTop: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
            })}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    width: '100%',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    {typeof status === 'string' && (
                        <StatusChip
                            label={status}
                            {...licenseStatusChipProps(status)}
                        />
                    )}
                    {isExpired && (
                        <StatusChip intent="error" label="Expired" />
                    )}
                </Box>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        justifyContent: 'flex-end',
                    }}
                >
                    {showApprove && (
                        <UpdateWithoutConfirmButton
                            label="Approve"
                            mutationMode="optimistic"
                            data={{status: LicenseStatus.APPROVED}}
                            icon={<CheckCircleOutlineIcon />}
                            variant="contained"
                            color="success"
                            mutationOptions={mutationOptions}
                        />
                    )}
                    {showReject && (
                        <UpdateWithoutConfirmButton
                            label="Reject"
                            mutationMode="optimistic"
                            data={{status: LicenseStatus.REJECTED}}
                            icon={<HighlightOffOutlinedIcon />}
                            variant="outlined"
                            color="error"
                            mutationOptions={mutationOptions}
                        />
                    )}
                </Box>
            </Box>
        </Toolbar>
    );
};

export const LicenseEdit = () => (
    <Grid container spacing={3} sx={{pb: 3}}>
        <Grid item xs={12} md={4}>
            <Edit title={<Title />} mutationMode="optimistic">
                <AsideForm
                    sx={(theme) => ({
                        ...shellSx(theme),
                        '& .MuiCardContent-root': {
                            p: 0,
                        },
                    })}
                >
                    <Box sx={{px: 2.5, pt: 2.5, pb: 2}}>
                        <Typography
                            variant="overline"
                            color="text.secondary"
                            sx={{letterSpacing: '0.1em', fontWeight: 600}}
                        >
                            Applicant
                        </Typography>
                        <ReferenceField
                            source="user_id"
                            reference="users"
                            link={false}
                        >
                            <FunctionField
                                render={(user) => (
                                    <Typography
                                        component="span"
                                        variant="h6"
                                        sx={{
                                            fontWeight: 700,
                                            display: 'block',
                                            mt: 0.5,
                                            letterSpacing: '-0.02em',
                                        }}
                                    >
                                        {formatUserDisplayName(user)}
                                    </Typography>
                                )}
                            />
                        </ReferenceField>
                    </Box>
                    <Divider />
                    <Box sx={{px: 2.5, py: 2}}>
                        <Grid container spacing={2}>
                            <LabeledGridItem xs={12} sx={{width: '100%'}}>
                                <ReferenceField
                                    source="user_id"
                                    reference="users"
                                    label="Email"
                                    link={false}
                                >
                                    <TextField
                                        source="email"
                                        emptyText="Not provided"
                                    />
                                </ReferenceField>
                            </LabeledGridItem>
                            <LabeledGridItem xs={12} sx={{width: '100%'}}>
                                <ReferenceField
                                    source="user_id"
                                    reference="users"
                                    label="Phone"
                                    link={false}
                                >
                                    <TextField
                                        source="phone_number"
                                        emptyText="Not provided"
                                    />
                                </ReferenceField>
                            </LabeledGridItem>
                        </Grid>
                    </Box>
                </AsideForm>
            </Edit>
        </Grid>
        <Grid item xs={12} md={8}>
            <Edit mutationMode="optimistic" title=" ">
                <SimpleForm
                    toolbar={<LicenseToolbar />}
                    sx={(theme) => ({
                        ...shellSx(theme),
                        '& .MuiCardContent-root': {
                            p: 0,
                        },
                    })}
                >
                    <Box sx={{px: 2.5, pt: 2.5, pb: 2}}>
                        <Typography
                            variant="overline"
                            color="text.secondary"
                            sx={{letterSpacing: '0.1em', fontWeight: 600}}
                        >
                            Licence
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                mt: 0.5,
                                letterSpacing: '-0.02em',
                            }}
                        >
                            Details & review
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.75}}>
                            Update the licence record, then approve or reject when
                            ready.
                        </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{px: 2.5, py: 2.5}}>
                        <Grid container spacing={2.5}>
                            <LabeledGridItem xs={12} md={6} sx={{width: '100%'}}>
                                <TextField
                                    source="organisation"
                                    label="Organisation"
                                />
                            </LabeledGridItem>
                            <LabeledGridItem xs={12} md={6} sx={{width: '100%'}}>
                                <TextField
                                    source="code"
                                    label="Licence number"
                                />
                            </LabeledGridItem>
                            <LabeledGridItem xs={12} md={6} sx={{width: '100%'}}>
                                <TextField
                                    source="postcode"
                                    label="Postcode"
                                    emptyText="Not provided"
                                />
                            </LabeledGridItem>
                            <LabeledGridItem xs={12} md={6} sx={{width: '100%'}}>
                                <ExpiryDateField source="expiry" label="Expiry" />
                            </LabeledGridItem>
                            <LabeledGridItem xs={12} sx={{width: '100%'}}>
                                <TextField source="status" label="Status" />
                            </LabeledGridItem>
                        </Grid>
                    </Box>
                </SimpleForm>
            </Edit>
        </Grid>
    </Grid>
);
